import { mapFromAdminTenant, mapFromAdminTenants } from 'mappings/adminTenantMapping';
import { mapFromDashboard } from 'mappings/dashboardMapping';
import { mapFromPartnerCustomerStats } from 'mappings/partnerCustomerStatsMapping';
import AdminTenant from 'models/adminTenant';
import Dashboard from 'models/dashboard';
import AdminTenantDTO from 'models/dto/adminTenantDTO';
import DashboardDTO from 'models/dto/dashboardDTO';
import PartnerCustomerStatsDTO from 'models/dto/partnerCustomerStatsDTO';
import GlobalDataCache from 'models/globalDataCache/globalDataCache';
import PartnerCustomerStats from 'models/partnerCustomerStats';
import { CustomerAccessStatus } from 'models/tenant';
import AppError from 'utils/appError';
import http from './httpService';

export async function apiGetTenantsForPartner(accessToken: string, cache: GlobalDataCache): Promise<AdminTenant[]> {
  try {
    //every endpoint in this service addresses the partner tenant and is guarded by RequirePartnerManager,
    //so the customer scope must never be applied to it
    const ar = await http.get<AdminTenantDTO[]>(
      `tenants/partner`,
      http.getRequestConfig(accessToken, undefined, true),
    );

    return mapFromAdminTenants(ar.data, cache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetPartnerTenant(
  id: string,
  accessToken: string,
  cache: GlobalDataCache,
): Promise<AdminTenant | undefined> {
  try {
    const ar = await http.get<AdminTenantDTO>(
      `tenants/partner/${id}`,
      http.getRequestConfig(accessToken, undefined, true),
    );

    return mapFromAdminTenant(ar.data, cache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetPartnerCustomerAccess(id: string, accessToken: string): Promise<CustomerAccessStatus> {
  try {
    //this is a partner-level check; force the partner context so a set customer scope does not cause a 403
    const ar = await http.get<number>(
      `tenants/partner/${id}/access`,
      http.getRequestConfig(accessToken, undefined, true),
    );

    return ar.data as CustomerAccessStatus;
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetPartnerAccessibleCustomerIds(accessToken: string): Promise<string[]> {
  try {
    //the customer ids whose data the dashboard widgets may reveal to this partner user; the customer list
    //minus this set is what the widgets leave out
    const ar = await http.get<string[]>(
      `tenants/partner/accessible`,
      http.getRequestConfig(accessToken, undefined, true),
    );

    return ar.data ?? [];
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetPartnerStats(accessToken: string, cache: GlobalDataCache): Promise<PartnerCustomerStats> {
  try {
    const ar = await http.get<PartnerCustomerStatsDTO>(
      `tenants/partner/stats`,
      http.getRequestConfig(accessToken, undefined, true),
    );

    return mapFromPartnerCustomerStats(ar.data, cache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiCreateTenantDashboard(
  id: string,
  accessToken: string,
  cache: GlobalDataCache,
): Promise<Dashboard> {
  try {
    const ar = await http.post<DashboardDTO>(
      `tenants/partner/${id}/dashboard`,
      null,
      http.getRequestConfig(accessToken, undefined, true),
    );

    return mapFromDashboard(ar.data, cache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}
